import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';

// UI Components
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '../ui/card';
import { Trophy } from 'lucide-react';

// Auth Context
import { useAuth } from '../../contexts/AuthContext';

// Constants
import { US_STATES, SPORTS_OPTIONS, POSITION_OPTIONS, EXPERIENCE_LEVELS } from './authConstants';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Initial form state
const INITIAL_FORM_STATE = {
  username: '',
  email: '',
  password: '',
  first_name: '',
  last_name: '',
  role: 'athlete',
  date_of_birth: '',
  parent_email: '',
  state: '',
  age: '',
  height_feet: '',
  height_inches: '',
  weight: '',
  focused_sport: '',
  primary_position: '',
  secondary_position: '',
  experience_level: '',
  competition_level: '',
  training_frequency: '',
  dominant_side: '',
  key_strengths: [],
  areas_to_improve: [],
  athletic_goals: [],
  injury_history: '',
  mental_training_experience: '',
  organization_name: '',
  coaching_sport: '',
  coaching_experience: '',
  team_level: '',
  number_of_players: '',
  agreed_to_terms: false
};

/**
 * AuthPage Component - Handles Login, Registration, and Email Verification
 */
const AuthPage = () => {
  const [isLogin, setIsLogin] = useState(true);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [step, setStep] = useState(1);
  const [maxSteps, setMaxSteps] = useState(5);
  const [formData, setFormData] = useState(INITIAL_FORM_STATE);
  const [forgotPasswordEmail, setForgotPasswordEmail] = useState('');
  const [useParentEmail, setUseParentEmail] = useState(false);
  const [hasParentEmail, setHasParentEmail] = useState(false);
  const [maskedParentEmail, setMaskedParentEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [showVerification, setShowVerification] = useState(false);
  const [verificationCode, setVerificationCode] = useState('');
  const [verificationEmail, setVerificationEmail] = useState('');
  
  const { login, register, user, verifyEmail, resendVerificationCode } = useAuth();
  const navigate = useNavigate();

  // Redirect if already authenticated
  useEffect(() => {
    if (user) {
      navigate('/dashboard');
    }
  }, [user, navigate]);

  // Adjust steps based on role
  useEffect(() => {
    setMaxSteps(formData.role === 'coach' ? 4 : 5);
  }, [formData.role]);

  // Calculate age from DOB (COPPA compliance)
  const calculateAge = (dateOfBirth) => {
    if (!dateOfBirth) return null;
    const today = new Date();
    const birthDate = new Date(dateOfBirth);
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    return age;
  };

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const resetForm = () => {
    setFormData(INITIAL_FORM_STATE);
    setStep(1);
  };

  const handleLogin = async (username, password) => {
    setLoading(true);
    const result = await login(username, password);
    setLoading(false);
    
    if (result.requiresVerification) {
      setVerificationEmail(result.email);
      setShowVerification(true);
      return;
    }
    
    if (result.success) {
      resetForm();
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (step < maxSteps) {
      setStep(step + 1);
      return;
    }
    
    setLoading(true);
    
    // COPPA Compliance validations
    if (!formData.date_of_birth) {
      toast.error('Date of birth is required');
      setLoading(false);
      return;
    }
    
    const userAge = calculateAge(formData.date_of_birth);
    if (userAge < 13) {
      toast.error('You must be at least 13 years old to use Grind.365.');
      setLoading(false);
      return;
    }
    
    if (!formData.state || formData.state.trim() === '') {
      toast.error('Please select your US state');
      setLoading(false);
      return;
    }
    
    // Prepare submission data
    const submitData = { ...formData };
    
    if (submitData.age && submitData.age.toString().trim()) {
      submitData.age = parseInt(submitData.age, 10);
    } else {
      delete submitData.age;
    }
    
    if (!submitData.parent_email || !submitData.parent_email.trim()) {
      delete submitData.parent_email;
    }
    
    if (submitData.number_of_players) {
      submitData.number_of_players = parseInt(submitData.number_of_players, 10);
    } else {
      delete submitData.number_of_players;
    }
    
    if (submitData.weight) {
      submitData.weight = `${submitData.weight} lbs`;
    }
    
    if (submitData.height_feet && submitData.height_inches) {
      submitData.height = `${submitData.height_feet}'${submitData.height_inches}"`;
    } else if (submitData.height_feet) {
      submitData.height = `${submitData.height_feet}'0"`;
    }
    delete submitData.height_feet;
    delete submitData.height_inches;
    
    const result = await register(submitData);
    setLoading(false);
    
    if (result?.success) {
      if (result.needsVerification) {
        setVerificationEmail(result.email);
        setShowVerification(true);
        toast.info('Please check your email for a verification code');
      } else {
        resetForm();
      }
    }
  };

  const handlePrevStep = () => {
    if (step > 1) setStep(step - 1);
  };

  const handleVerifyEmail = async (e) => {
    e.preventDefault();
    if (!verificationCode || verificationCode.length !== 6) {
      toast.error('Please enter a valid 6-digit code');
      return;
    }
    
    setLoading(true);
    const success = await verifyEmail(verificationEmail, verificationCode);
    setLoading(false);
    
    if (success) {
      setShowVerification(false);
      setVerificationCode('');
      setVerificationEmail('');
      resetForm();
    }
  };

  const handleResendCode = async () => {
    setLoading(true);
    await resendVerificationCode(verificationEmail);
    setLoading(false);
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await axios.post(`${API}/auth/forgot-password`, {
        email: forgotPasswordEmail,
        use_parent_email: useParentEmail
      });
      
      if (response.data.error === 'no_parent_email') {
        toast.error('No backup email found for this account. Please contact support.');
      } else if (response.data.sent_to === 'parent') {
        toast.success(`Password reset link sent to your parent/guardian email (${response.data.masked_email}).`);
        setShowForgotPassword(false);
        setForgotPasswordEmail('');
        setUseParentEmail(false);
      } else {
        toast.success('Password reset instructions sent to your email');
        setShowForgotPassword(false);
        setForgotPasswordEmail('');
        setUseParentEmail(false);
      }
    } catch (error) {
      toast.error('Failed to send reset instructions');
    } finally {
      setLoading(false);
    }
  };

  const checkParentEmail = async (email) => {
    if (!email || !email.includes('@')) {
      setHasParentEmail(false);
      setMaskedParentEmail('');
      return;
    }
    try {
      const response = await axios.get(`${API}/auth/check-parent-email/${encodeURIComponent(email)}`);
      setHasParentEmail(response.data.has_parent_email);
      setMaskedParentEmail(response.data.masked_parent_email || '');
    } catch (error) {
      setHasParentEmail(false);
      setMaskedParentEmail('');
    }
  };

  // Get step description
  const getStepDescription = () => {
    if (showForgotPassword) return 'Reset your password';
    if (isLogin) return 'Welcome back, champion!';
    if (step === 1) return 'Start your athletic journey today';
    if (step === 2) return 'Tell us about your physical profile';
    if (step === 3) return formData.role === 'coach' ? 'Coach and organization details' : 'Sport-specific information';
    if (step === 4) return formData.role === 'coach' ? 'Ready to create your account' : 'Identify your key strengths';
    return 'Areas you want to improve';
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-900 via-blue-800 to-blue-950 flex items-center justify-center p-4">
      <Card className="w-full max-w-md shadow-xl border-0 bg-blue-900/95 backdrop-blur-sm border border-blue-700">
        <CardHeader className="space-y-1 text-center">
          <div className="mx-auto w-16 h-16 bg-yellow-500 rounded-full flex items-center justify-center mb-4">
            <Trophy className="w-8 h-8 text-blue-900" />
          </div>
          <CardTitle className="text-2xl font-bold text-white">Grind.365</CardTitle>
          <CardDescription className="text-gray-200">{getStepDescription()}</CardDescription>
        </CardHeader>
        
        <CardContent>
          {showVerification ? (
            <VerificationForm
              verificationEmail={verificationEmail}
              verificationCode={verificationCode}
              setVerificationCode={setVerificationCode}
              handleVerifyEmail={handleVerifyEmail}
              handleResendCode={handleResendCode}
              loading={loading}
            />
          ) : showForgotPassword ? (
            <ForgotPasswordForm
              forgotPasswordEmail={forgotPasswordEmail}
              setForgotPasswordEmail={setForgotPasswordEmail}
              useParentEmail={useParentEmail}
              setUseParentEmail={setUseParentEmail}
              hasParentEmail={hasParentEmail}
              maskedParentEmail={maskedParentEmail}
              checkParentEmail={checkParentEmail}
              handleForgotPassword={handleForgotPassword}
              setShowForgotPassword={setShowForgotPassword}
              loading={loading}
            />
          ) : (
            <MainAuthForm
              isLogin={isLogin}
              step={step}
              maxSteps={maxSteps}
              formData={formData}
              setFormData={setFormData}
              handleInputChange={handleInputChange}
              handleLogin={handleLogin}
              handleSubmit={handleSubmit}
              handlePrevStep={handlePrevStep}
              setShowForgotPassword={setShowForgotPassword}
              loading={loading}
              calculateAge={calculateAge}
            />
          )}
        </CardContent>
        
        <CardFooter className="justify-center">
          {!showForgotPassword && !showVerification && (
            <Button
              variant="ghost"
              onClick={() => { setIsLogin(!isLogin); setStep(1); }}
              className="text-yellow-400"
            >
              {isLogin ? "Don't have an account? Sign up" : 'Already have an account? Sign in'}
            </Button>
          )}
        </CardFooter>
      </Card>
    </div>
  );
};

// Sub-components for cleaner organization

const VerificationForm = ({ verificationEmail, verificationCode, setVerificationCode, handleVerifyEmail, handleResendCode, loading }) => (
  <form onSubmit={handleVerifyEmail} className="space-y-4">
    <div className="text-center mb-4">
      <div className="text-yellow-400 text-sm mb-2">We've sent a verification code to:</div>
      <div className="text-white font-semibold">{verificationEmail}</div>
    </div>
    
    <div className="space-y-2">
      <Label htmlFor="verification_code" className="text-white">Verification Code</Label>
      <Input
        id="verification_code"
        type="text"
        value={verificationCode}
        onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
        required
        maxLength={6}
        className="border-blue-700 focus:border-yellow-500 bg-blue-900/50 text-white text-center text-2xl tracking-widest font-bold"
        placeholder="000000"
      />
      <p className="text-xs text-gray-400 text-center">Enter the 6-digit code from your email</p>
    </div>
    
    <Button type="submit" className="w-full bg-yellow-500 hover:bg-yellow-600 text-blue-900 font-semibold" disabled={loading}>
      {loading ? 'Verifying...' : 'Verify Email'}
    </Button>
    
    <div className="text-center">
      <Button type="button" variant="ghost" onClick={handleResendCode} disabled={loading} className="text-yellow-400 text-sm">
        Didn't receive the code? Resend
      </Button>
    </div>
  </form>
);

const ForgotPasswordForm = ({ forgotPasswordEmail, setForgotPasswordEmail, useParentEmail, setUseParentEmail, hasParentEmail, maskedParentEmail, checkParentEmail, handleForgotPassword, setShowForgotPassword, loading }) => (
  <form onSubmit={handleForgotPassword} className="space-y-4">
    <div className="space-y-2">
      <Label htmlFor="forgot_email" className="text-white">Email Address</Label>
      <Input
        id="forgot_email"
        type="email"
        value={forgotPasswordEmail}
        onChange={(e) => {
          setForgotPasswordEmail(e.target.value);
          checkParentEmail(e.target.value);
        }}
        required
        className="border-blue-700 focus:border-yellow-500 bg-blue-900/50 text-white"
        placeholder="Enter your email"
      />
    </div>
    
    {hasParentEmail && (
      <div className="p-3 bg-blue-800/50 rounded-lg border border-blue-600">
        <label className="flex items-center space-x-2 cursor-pointer">
          <input
            type="checkbox"
            checked={useParentEmail}
            onChange={(e) => setUseParentEmail(e.target.checked)}
            className="rounded border-yellow-500"
          />
          <span className="text-sm text-white">
            Send to parent/guardian email ({maskedParentEmail})
          </span>
        </label>
      </div>
    )}
    
    <Button type="submit" className="w-full bg-yellow-500 hover:bg-yellow-600 text-blue-900 font-semibold" disabled={loading}>
      {loading ? 'Sending...' : 'Send Reset Link'}
    </Button>
    
    <Button type="button" variant="ghost" onClick={() => setShowForgotPassword(false)} className="w-full text-yellow-400">
      Back to Login
    </Button>
  </form>
);

const MainAuthForm = ({ isLogin, step, maxSteps, formData, setFormData, handleInputChange, handleLogin, handleSubmit, handlePrevStep, setShowForgotPassword, loading, calculateAge }) => {
  const [loginUsername, setLoginUsername] = useState('');
  const [loginPassword, setLoginPassword] = useState('');

  const onLoginSubmit = (e) => {
    e.preventDefault();
    handleLogin(loginUsername, loginPassword);
  };

  if (isLogin) {
    return (
      <form onSubmit={onLoginSubmit} className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="login_username" className="text-white">Username</Label>
          <Input
            id="login_username"
            value={loginUsername}
            onChange={(e) => setLoginUsername(e.target.value)}
            required
            className="border-blue-700 focus:border-yellow-500 bg-blue-900/50 text-white"
            placeholder="Username"
            data-testid="login-username-input"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="login_password" className="text-white">Password</Label>
          <Input
            id="login_password"
            type="password"
            value={loginPassword}
            onChange={(e) => setLoginPassword(e.target.value)}
            required
            className="border-blue-700 focus:border-yellow-500 bg-blue-900/50 text-white"
            placeholder="Password"
            data-testid="login-password-input"
          />
        </div>
        <Button
          type="button"
          variant="link"
          onClick={() => setShowForgotPassword(true)}
          className="text-yellow-400 text-sm p-0 h-auto"
        >
          Forgot password?
        </Button>
        <Button type="submit" className="w-full bg-yellow-500 hover:bg-yellow-600 text-blue-900 font-semibold" disabled={loading} data-testid="login-submit-button">
          {loading ? 'Loading...' : 'Sign In'}
        </Button>
      </form>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <RegistrationSteps
        step={step}
        maxSteps={maxSteps}
        formData={formData}
        setFormData={setFormData}
        handleInputChange={handleInputChange}
        calculateAge={calculateAge}
      />
      
      {step > 1 && (
        <Button type="button" onClick={handlePrevStep} variant="outline" className="w-full mb-2 border-emerald-300 text-yellow-400 hover:bg-blue-900">
          Previous
        </Button>
      )}
      
      <Button
        type="submit"
        className="w-full bg-yellow-500 hover:bg-yellow-600 text-blue-900 font-semibold"
        disabled={
          loading ||
          (step === 4 && formData.role === 'athlete' && formData.key_strengths.length < 3) ||
          (step === 5 && formData.role === 'athlete' && formData.areas_to_improve.length < 3) ||
          (step === maxSteps && !formData.agreed_to_terms)
        }
      >
        {loading ? 'Loading...' : step < maxSteps ? `Next (${step}/${maxSteps})` : 'Create Account'}
      </Button>
    </form>
  );
};

// Registration Steps Component
const RegistrationSteps = ({ step, maxSteps, formData, setFormData, handleInputChange, calculateAge }) => {
  const userAge = calculateAge(formData.date_of_birth);
  
  return (
    <>
      {/* Step 1: Basic Info */}
      {step === 1 && (
        <Step1BasicInfo formData={formData} handleInputChange={handleInputChange} userAge={userAge} />
      )}
      
      {/* Step 2: Physical Profile */}
      {step === 2 && (
        <Step2PhysicalProfile formData={formData} setFormData={setFormData} handleInputChange={handleInputChange} />
      )}
      
      {/* Step 3: Sport/Coach Details */}
      {step === 3 && (
        <Step3Details formData={formData} setFormData={setFormData} handleInputChange={handleInputChange} />
      )}
      
      {/* Step 4: Strengths (athlete) or Final (coach) */}
      {step === 4 && formData.role === 'athlete' && (
        <Step4Strengths formData={formData} setFormData={setFormData} />
      )}
      
      {/* Step 5: Areas to Improve (athlete) */}
      {step === 5 && formData.role === 'athlete' && (
        <Step5Improvements formData={formData} setFormData={setFormData} />
      )}
      
      {/* Coach Final Step */}
      {step === 4 && formData.role === 'coach' && (
        <CoachFinalStep />
      )}
      
      {/* Terms and Conditions - Final step */}
      {((step === 5 && formData.role === 'athlete') || (step === 4 && formData.role === 'coach')) && (
        <TermsCheckbox formData={formData} setFormData={setFormData} />
      )}
    </>
  );
};

// Individual Step Components
const Step1BasicInfo = ({ formData, handleInputChange, userAge }) => (
  <>
    <div className="grid grid-cols-2 gap-4">
      <div className="space-y-2">
        <Label htmlFor="first_name" className="text-white">First Name</Label>
        <Input id="first_name" name="first_name" value={formData.first_name} onChange={handleInputChange} required className="border-blue-700 focus:border-yellow-500 bg-blue-900/50 text-white" />
      </div>
      <div className="space-y-2">
        <Label htmlFor="last_name" className="text-white">Last Name</Label>
        <Input id="last_name" name="last_name" value={formData.last_name} onChange={handleInputChange} required className="border-blue-700 focus:border-yellow-500 bg-blue-900/50 text-white" />
      </div>
    </div>
    
    <div className="space-y-2">
      <Label htmlFor="username" className="text-white">Username</Label>
      <Input id="username" name="username" value={formData.username} onChange={handleInputChange} required className="border-blue-700 focus:border-yellow-500 bg-blue-900/50 text-white" />
    </div>
    
    <div className="space-y-2">
      <Label htmlFor="email" className="text-white">Email</Label>
      <Input id="email" name="email" type="email" value={formData.email} onChange={handleInputChange} required className="border-blue-700 focus:border-yellow-500 bg-blue-900/50 text-white" />
    </div>
    
    <div className="space-y-2">
      <Label htmlFor="password" className="text-white">Password</Label>
      <Input id="password" name="password" type="password" value={formData.password} onChange={handleInputChange} required className="border-blue-700 focus:border-yellow-500 bg-blue-900/50 text-white" />
      <p className="text-xs text-gray-400">8+ characters with uppercase, lowercase, number, and special character</p>
    </div>
    
    <div className="space-y-2">
      <Label htmlFor="date_of_birth" className="text-white">Date of Birth</Label>
      <Input id="date_of_birth" name="date_of_birth" type="date" value={formData.date_of_birth} onChange={handleInputChange} required className="border-blue-700 focus:border-yellow-500 bg-blue-900/50 text-white" />
      {userAge !== null && <p className="text-xs text-yellow-400">Age: {userAge} years old</p>}
    </div>
    
    <div className="space-y-2">
      <Label htmlFor="parent_email" className="text-white">Parent/Guardian Email <span className="text-gray-400">(optional)</span></Label>
      <Input id="parent_email" name="parent_email" type="email" value={formData.parent_email} onChange={handleInputChange} className="border-blue-700 focus:border-yellow-500 bg-blue-900/50 text-white" placeholder="For password recovery" />
    </div>
    
    <div className="space-y-2">
      <Label htmlFor="state" className="text-white">US State</Label>
      <select id="state" name="state" value={formData.state} onChange={handleInputChange} required className="w-full px-3 py-2 border border-blue-700 rounded-md focus:border-yellow-500 bg-blue-900/50 text-white">
        <option value="">Select your state</option>
        {US_STATES.map(state => <option key={state.value} value={state.value}>{state.label}</option>)}
      </select>
    </div>
    
    <div className="space-y-2">
      <Label className="text-white">I am a...</Label>
      <div className="flex gap-4">
        {['athlete', 'coach'].map(role => (
          <label key={role} className="flex items-center space-x-2 cursor-pointer">
            <input type="radio" name="role" value={role} checked={formData.role === role} onChange={handleInputChange} className="text-yellow-500" />
            <span className="text-white capitalize">{role}</span>
          </label>
        ))}
      </div>
    </div>
  </>
);

const Step2PhysicalProfile = ({ formData, setFormData, handleInputChange }) => (
  <>
    <div className="space-y-2">
      <Label className="text-white">Height</Label>
      <div className="flex gap-2">
        <select name="height_feet" value={formData.height_feet} onChange={handleInputChange} className="flex-1 px-3 py-2 border border-blue-700 rounded-md bg-blue-900/50 text-white">
          <option value="">Feet</option>
          {[4, 5, 6, 7].map(ft => <option key={`ft-${ft}`} value={ft}>{ft}'</option>)}
        </select>
        <select name="height_inches" value={formData.height_inches} onChange={handleInputChange} className="flex-1 px-3 py-2 border border-blue-700 rounded-md bg-blue-900/50 text-white">
          <option value="">Inches</option>
          {[...Array(12)].map((_, i) => <option key={`in-${i}`} value={i}>{i}"</option>)}
        </select>
      </div>
    </div>
    
    <div className="space-y-2">
      <Label htmlFor="weight" className="text-white">Weight (lbs)</Label>
      <Input id="weight" name="weight" type="number" value={formData.weight} onChange={handleInputChange} className="border-blue-700 focus:border-yellow-500 bg-blue-900/50 text-white" placeholder="e.g., 150" />
    </div>
    
    <div className="space-y-2">
      <Label htmlFor="focused_sport" className="text-white">Primary Sport</Label>
      <select id="focused_sport" name="focused_sport" value={formData.focused_sport} onChange={handleInputChange} className="w-full px-3 py-2 border border-blue-700 rounded-md bg-blue-900/50 text-white">
        <option value="">Select sport</option>
        {SPORTS_OPTIONS.map(sport => <option key={sport.value} value={sport.value}>{sport.label}</option>)}
      </select>
    </div>
    
    <div className="space-y-2">
      <Label className="text-white">Dominant Side</Label>
      <div className="flex gap-4">
        {['right', 'left', 'ambidextrous'].map(side => (
          <label key={side} className="flex items-center space-x-2 cursor-pointer">
            <input type="radio" name="dominant_side" value={side} checked={formData.dominant_side === side} onChange={handleInputChange} className="text-yellow-500" />
            <span className="text-white capitalize">{side}</span>
          </label>
        ))}
      </div>
    </div>
  </>
);

const Step3Details = ({ formData, setFormData, handleInputChange }) => {
  if (formData.role === 'coach') {
    return (
      <>
        <div className="space-y-2">
          <Label htmlFor="organization_name" className="text-white">Organization/School Name</Label>
          <Input id="organization_name" name="organization_name" value={formData.organization_name} onChange={handleInputChange} className="border-blue-700 focus:border-yellow-500 bg-blue-900/50 text-white" />
        </div>
        
        <div className="space-y-2">
          <Label htmlFor="coaching_sport" className="text-white">Sport You Coach</Label>
          <select id="coaching_sport" name="coaching_sport" value={formData.coaching_sport} onChange={handleInputChange} className="w-full px-3 py-2 border border-blue-700 rounded-md bg-blue-900/50 text-white">
            <option value="">Select sport</option>
            {SPORTS_OPTIONS.map(sport => <option key={sport.value} value={sport.value}>{sport.label}</option>)}
          </select>
        </div>
        
        <div className="space-y-2">
          <Label htmlFor="team_level" className="text-white">Team Level</Label>
          <select id="team_level" name="team_level" value={formData.team_level} onChange={handleInputChange} className="w-full px-3 py-2 border border-blue-700 rounded-md bg-blue-900/50 text-white">
            <option value="">Select level</option>
            <option value="youth">Youth (Under 12)</option>
            <option value="middle_school">Middle School</option>
            <option value="high_school">High School</option>
            <option value="club">Club/Travel</option>
            <option value="college">College</option>
            <option value="professional">Professional</option>
          </select>
        </div>
        
        <div className="space-y-2">
          <Label htmlFor="number_of_players" className="text-white">Number of Players</Label>
          <Input id="number_of_players" name="number_of_players" type="number" value={formData.number_of_players} onChange={handleInputChange} className="border-blue-700 focus:border-yellow-500 bg-blue-900/50 text-white" placeholder="e.g., 15" />
        </div>
      </>
    );
  }
  
  // Athlete step 3
  const positions = POSITION_OPTIONS[formData.focused_sport] || [];
  
  return (
    <>
      <div className="space-y-2">
        <Label htmlFor="primary_position" className="text-white">Primary Position</Label>
        <select id="primary_position" name="primary_position" value={formData.primary_position} onChange={handleInputChange} className="w-full px-3 py-2 border border-blue-700 rounded-md bg-blue-900/50 text-white">
          <option value="">Select position</option>
          {positions.map(pos => <option key={pos.value} value={pos.value}>{pos.label}</option>)}
        </select>
      </div>
      
      <div className="space-y-2">
        <Label htmlFor="experience_level" className="text-white">Experience Level</Label>
        <select id="experience_level" name="experience_level" value={formData.experience_level} onChange={handleInputChange} className="w-full px-3 py-2 border border-blue-700 rounded-md bg-blue-900/50 text-white">
          <option value="">Select level</option>
          {EXPERIENCE_LEVELS.map(level => <option key={level.value} value={level.value}>{level.label}</option>)}
        </select>
      </div>
      
      <div className="space-y-2">
        <Label htmlFor="competition_level" className="text-white">Competition Level</Label>
        <select id="competition_level" name="competition_level" value={formData.competition_level} onChange={handleInputChange} className="w-full px-3 py-2 border border-blue-700 rounded-md bg-blue-900/50 text-white">
          <option value="">Select level</option>
          <option value="recreational">Recreational</option>
          <option value="school">School Team</option>
          <option value="club">Club/Travel</option>
          <option value="elite">Elite/Select</option>
        </select>
      </div>
    </>
  );
};

const Step4Strengths = ({ formData, setFormData }) => {
  const strengthOptions = ['Speed', 'Endurance', 'Power', 'Agility', 'Flexibility', 'Mental Toughness', 'Leadership', 'Teamwork', 'Technique', 'Game IQ'];
  
  const toggleStrength = (strength) => {
    const current = formData.key_strengths || [];
    if (current.includes(strength)) {
      setFormData({ ...formData, key_strengths: current.filter(s => s !== strength) });
    } else if (current.length < 5) {
      setFormData({ ...formData, key_strengths: [...current, strength] });
    }
  };
  
  return (
    <div className="space-y-2">
      <Label className="text-white">Select Your Top 3-5 Strengths</Label>
      <div className="grid grid-cols-2 gap-2">
        {strengthOptions.map(strength => (
          <button
            key={strength}
            type="button"
            onClick={() => toggleStrength(strength)}
            className={`p-2 rounded-lg text-sm font-medium transition-colors ${
              formData.key_strengths?.includes(strength)
                ? 'bg-yellow-500 text-blue-900'
                : 'bg-blue-800 text-white hover:bg-blue-700'
            }`}
          >
            {strength}
          </button>
        ))}
      </div>
      <p className="text-xs text-gray-400">Selected: {formData.key_strengths?.length || 0}/5</p>
    </div>
  );
};

const Step5Improvements = ({ formData, setFormData }) => {
  const improvementOptions = ['Speed', 'Endurance', 'Strength', 'Flexibility', 'Mental Focus', 'Confidence', 'Consistency', 'Recovery', 'Nutrition', 'Sleep'];
  
  const toggleImprovement = (item) => {
    const current = formData.areas_to_improve || [];
    if (current.includes(item)) {
      setFormData({ ...formData, areas_to_improve: current.filter(i => i !== item) });
    } else if (current.length < 5) {
      setFormData({ ...formData, areas_to_improve: [...current, item] });
    }
  };
  
  return (
    <div className="space-y-2">
      <Label className="text-white">Select 3-5 Areas to Improve</Label>
      <div className="grid grid-cols-2 gap-2">
        {improvementOptions.map(item => (
          <button
            key={item}
            type="button"
            onClick={() => toggleImprovement(item)}
            className={`p-2 rounded-lg text-sm font-medium transition-colors ${
              formData.areas_to_improve?.includes(item)
                ? 'bg-yellow-500 text-blue-900'
                : 'bg-blue-800 text-white hover:bg-blue-700'
            }`}
          >
            {item}
          </button>
        ))}
      </div>
      <p className="text-xs text-gray-400">Selected: {formData.areas_to_improve?.length || 0}/5</p>
    </div>
  );
};

const CoachFinalStep = () => (
  <div className="text-center space-y-4">
    <div className="p-4 bg-blue-900 rounded-lg">
      <h3 className="font-semibold text-white mb-2">Ready to Get Started!</h3>
      <p className="text-slate-50 text-sm">After creating your account, you'll be able to:</p>
      <ul className="text-yellow-400 text-sm mt-2 space-y-1">
        <li>• Send email invites to your players</li>
        <li>• Track team progress and engagement</li>
        <li>• Access coach-specific resources</li>
        <li>• Monitor individual athlete development</li>
      </ul>
    </div>
  </div>
);

const TermsCheckbox = ({ formData, setFormData }) => (
  <div className="p-4 bg-blue-900/50 rounded-lg border border-yellow-500/30">
    <label className="flex items-start space-x-3 cursor-pointer">
      <input
        type="checkbox"
        checked={formData.agreed_to_terms}
        onChange={(e) => setFormData({ ...formData, agreed_to_terms: e.target.checked })}
        className="mt-1 rounded border-yellow-500 text-yellow-500"
        required
      />
      <span className="text-sm text-white">
        I agree to the{' '}
        <a href="/terms-and-conditions.txt" target="_blank" rel="noopener noreferrer" className="text-yellow-400 hover:text-yellow-300 underline font-semibold">
          Terms and Conditions
        </a>
        {' '}and consent to the collection and use of my data as described.
      </span>
    </label>
  </div>
);

export default AuthPage;
